/**
 * Type definitions for Natalon Mini App
 */

// Birth data for chart calculation
export interface BirthData {
  name?: string;
  date: string; // YYYY-MM-DD
  time: string; // HH:MM
  timeKnown: boolean;
  location: LocationData;
}

// Location data from geocoding
export interface LocationData {
  displayName: string;
  displayNameShort: string;
  latitude: number;
  longitude: number;
  countryCode?: string;
  timezoneStr?: string;
}

// Planet position in the chart
export interface PlanetPosition {
  name: string;
  nameRu: string;
  sign: string;
  signRu: string;
  signNum: number;
  degree: number;
  longitude: number;
  latitude: number;
  speed: number;
  retrograde: boolean;
  house: number;
}

// House cusp
export interface HouseCusp {
  number: number;
  sign: string;
  signRu: string;
  signNum: number;
  degree: number;
  longitude: number;
}

// Aspect between planets
export interface Aspect {
  planet1: string;
  planet2: string;
  type: string;
  typeRu: string;
  angle: number;
  orb: number;
  applying: boolean;
}

// Special points (ASC, MC, etc.)
export interface ChartPoint {
  name: string;
  nameRu: string;
  sign: string;
  signRu: string;
  signNum: number;
  degree: number;
  longitude: number;
}

// Element balance
export interface ElementBalance {
  fire: number;
  earth: number;
  air: number;
  water: number;
}

// Mode balance
export interface ModeBalance {
  cardinal: number;
  fixed: number;
  mutable: number;
}

// Full natal chart data
export interface NatalChart {
  id?: string;
  birthData?: BirthData;
  planets: Record<string, PlanetPosition>;
  houses: Record<string, HouseCusp>;
  aspects: Aspect[];
  points: Record<string, ChartPoint>;
  elementBalance: ElementBalance;
  modeBalance: ModeBalance;
  dignities?: Record<string, string>;
  timezone?: string;
  utcOffset?: string; // e.g., "+03:00"
  destinyMatrix?: DestinyMatrixData;
  createdAt?: string;
}

export interface DestinyMatrixData {
  positions: Record<string, number>;
  lines: Record<string, number[]>;
  center: number;
  family_center: number;
}

// API response types
export interface LocationSearchResult {
  displayName: string;
  displayNameShort: string;
  latitude: number;
  longitude: number;
  countryCode?: string;
  timezoneStr?: string;
}

export interface ChartCalculationRequest {
  birthDate: string;
  birthTime: string;
  latitude: number;
  longitude: number;
  houseSystem?: string;
}

export interface ChartCalculationResponse {
  success: boolean;
  chart?: NatalChart;
  error?: string;
}

// Saved chart summary for list view
export interface SavedChartSummary {
  id: string;
  name: string | null;
  birthDate: string;
  birthTime: string;
  locationName: string;
  sunSign: string;
  sunSignRu: string;
  moonSign: string;
  moonSignRu: string;
  createdAt: string;
  isPrimary: boolean;
}

// Response for user charts list
export interface UserChartsResponse {
  success: boolean;
  charts: SavedChartSummary[];
  error?: string;
}

// Chart save request (to persist in DB)
export interface ChartSaveRequest {
  name?: string;
  birthDate: string; // YYYY-MM-DD
  birthTime: string; // HH:MM
  birthTimeKnown: boolean;
  latitude: number;
  longitude: number;
  locationName: string;
  houseSystem: string;
  planets: Record<string, PlanetPosition>;
  houses: Record<string, HouseCusp>;
  aspects: Aspect[];
  points: Record<string, ChartPoint>;
  elementBalance: ElementBalance;
  modeBalance: ModeBalance;
  dignities: Record<string, string>;
  timezone: string;
  utcOffset: number; // minutes
}

// Chart save response
export interface ChartSaveResponse {
  success: boolean;
  chartId?: string;
  error?: string;
}

// Interpretation topics
export type InterpretationTopic = string;

export interface InterpretationRequest {
  chartId: string;
  topic: InterpretationTopic;
}

// Topic metadata from backend
export interface TopicInfo {
  key: string;
  name: string;
  description: string;
  icon: string;
  isPaid: boolean;
  order: number;
}

export interface TopicsResponse {
  success: boolean;
  topics: TopicInfo[];
}

// Zodiac signs
export const ZODIAC_SIGNS = [
  { name: 'aries', nameRu: 'Овен', symbol: '♈' },
  { name: 'taurus', nameRu: 'Телец', symbol: '♉' },
  { name: 'gemini', nameRu: 'Близнецы', symbol: '♊' },
  { name: 'cancer', nameRu: 'Рак', symbol: '♋' },
  { name: 'leo', nameRu: 'Лев', symbol: '♌' },
  { name: 'virgo', nameRu: 'Дева', symbol: '♍' },
  { name: 'libra', nameRu: 'Весы', symbol: '♎' },
  { name: 'scorpio', nameRu: 'Скорпион', symbol: '♏' },
  { name: 'sagittarius', nameRu: 'Стрелец', symbol: '♐' },
  { name: 'capricorn', nameRu: 'Козерог', symbol: '♑' },
  { name: 'aquarius', nameRu: 'Водолей', symbol: '♒' },
  { name: 'pisces', nameRu: 'Рыбы', symbol: '♓' },
] as const;

// Planet symbols
export const PLANET_SYMBOLS: Record<string, string> = {
  sun: '☉',
  moon: '☽',
  mercury: '☿',
  venus: '♀',
  mars: '♂',
  jupiter: '♃',
  saturn: '♄',
  uranus: '♅',
  neptune: '♆',
  pluto: '♇',
  north_node: '☊',
  south_node: '☋',
  chiron: '⚷',
  lilith: '⚸',
};

// Aspect symbols
export const ASPECT_SYMBOLS: Record<string, string> = {
  conjunction: '☌',
  opposition: '☍',
  trine: '△',
  square: '□',
  sextile: '⚹',
  quincunx: '⚻',
};

// Family types
export interface FamilyMember {
  id: number;
  chartId: string;
  role: string;
  displayName: string | null;
  sunSignRu: string | null;
}

export interface FamilyGroup {
  id: number;
  name: string;
  forecastEnabled: boolean;
  members: FamilyMember[];
}

export interface FamilyResponse {
  success: boolean;
  family: FamilyGroup | null;
  error?: string;
}

export interface ToggleForecastResponse {
  success: boolean;
  forecastEnabled: boolean;
  error?: string;
}

export interface FamilyForecastResponse {
  success: boolean;
  forecast: string | null;
  error?: string;
}

export type FamilyRole = 'self' | 'spouse' | 'child' | 'parent' | 'sibling' | 'other';

export const FAMILY_ROLE_LABELS: Record<FamilyRole, string> = {
  self: 'Я',
  spouse: 'Супруг(а)',
  child: 'Ребёнок',
  parent: 'Родитель',
  sibling: 'Брат/сестра',
  other: 'Другой',
};

// Event subscription
export interface EventSubscription {
  eventType: string;
  enabled: boolean;
}

// User settings
export interface UserSettings {
  timezone: string | null;
  notificationHour: number;
  eventNotificationsEnabled: boolean;
  eventDaysBefore: number;
  forecastEnabled: boolean;
  primaryChartTimezone: string | null;
  subscriptions: EventSubscription[];
}

export interface UserSettingsResponse {
  success: boolean;
  settings?: UserSettings;
  error?: string;
}

export interface SubscriptionUpdate {
  eventType: string;
  enabled: boolean;
}

export interface UserSettingsUpdateRequest {
  timezone?: string | null;
  notificationHour?: number;
  eventNotificationsEnabled?: boolean;
  eventDaysBefore?: number;
  forecastEnabled?: boolean;
  subscriptions?: SubscriptionUpdate[];
}

export interface TimezoneOption {
  value: string;  // e.g., "Europe/Moscow"
  label: string;  // e.g., "Москва (UTC+3)"
}

export interface TimezonesResponse {
  success: boolean;
  timezones: TimezoneOption[];
}
